// Support One-Pager (progress-views design §7) — the screen half. The model is
// src/progress/onePager.ts; every sentence comes from the dictionary via
// src/progress/text.ts. Meaning is carried by words, numbers and shape
// (hollow = start, filled = end on the area dumbbells), never by colour alone.

import type { ReactNode } from "react";
import { fill, type Locale, type PortalDictionary } from "../../i18n/dictionary";
import type { OnePagerModel } from "../../progress/onePager";
import {
  bedLabel,
  formatDay,
  lastPractisedLine,
  practiceLines,
  stageLabel,
  stepLine,
  supportLines,
} from "../../progress/text";

type P = PortalDictionary["service"]["progress"];

export function SectionTitle({ children, id }: { children: ReactNode; id?: string }) {
  return (
    <h2
      id={id}
      style={{
        fontFamily: "var(--font-display)",
        fontWeight: 700,
        fontSize: 13,
        letterSpacing: ".12em",
        textTransform: "uppercase",
        color: "var(--ink-faint)",
        margin: "0 0 12px",
      }}
    >
      {children}
    </h2>
  );
}

const card = {
  background: "var(--surface)",
  border: "1px solid var(--line)",
  borderRadius: "var(--r-lg)",
  padding: "16px 18px",
} as const;

function Block({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div style={{ ...card, minWidth: 0 }}>
      <h3
        style={{
          fontFamily: "var(--font-display)",
          fontWeight: 700,
          fontSize: 15.5,
          color: "var(--ink-deep)",
          margin: "0 0 10px",
        }}
      >
        {title}
      </h3>
      {children}
    </div>
  );
}

const listStyle = { margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 10 } as const;
const lineStyle = { fontSize: 14.5, color: "var(--ink)", lineHeight: 1.5 } as const;
const metaStyle = { display: "block", fontSize: 12.5, color: "var(--ink-faint)", marginTop: 2 } as const;

function Dumbbells({ p, model }: { p: P; model: OnePagerModel }) {
  const max = Math.max(4, ...model.areas.map((a) => a.end));
  const W = 200;
  const x = (v: number) => 8 + (v / max) * (W - 16);
  return (
    <ul style={listStyle}>
      {model.areas.map((a) => {
        const name = bedLabel(p, a.domain);
        const label = fill(p.areaAria, { area: name, start: a.start, end: a.end });
        return (
          <li
            key={a.domain}
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr) minmax(90px, 1.3fr) auto",
              alignItems: "center",
              gap: 10,
            }}
          >
            <span style={{ fontSize: 14, color: "var(--ink)", minWidth: 0 }}>{name}</span>
            <svg
              viewBox={`0 0 ${W} 20`}
              width="100%"
              height="20"
              role="img"
              aria-label={label}
              preserveAspectRatio="none"
            >
              <title>{label}</title>
              <line x1="8" x2={W - 8} y1="10" y2="10" stroke="var(--line-strong)" strokeWidth="2" />
              <line x1={x(a.start)} x2={x(a.end)} y1="10" y2="10" stroke="var(--brand)" strokeWidth="4" />
              {/* start: hollow ring; end: filled dot — shape, not colour, tells them apart */}
              <circle cx={x(a.start)} cy="10" r="5" fill="var(--surface)" stroke="var(--ink-soft)" strokeWidth="2" />
              <circle cx={x(a.end)} cy="10" r="5.5" fill="var(--brand)" stroke="var(--brand)" strokeWidth="1" />
            </svg>
            <span aria-hidden="true" style={{ fontSize: 13.5, color: "var(--ink-soft)", whiteSpace: "nowrap" }}>
              {a.start} → {a.end}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

export function OnePagerView({
  model,
  locale,
  dict,
}: {
  model: OnePagerModel;
  locale: Locale;
  dict: PortalDictionary;
}) {
  const p = dict.service.progress;
  return (
    <section aria-labelledby="onepager-title" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
        <div>
          <SectionTitle id="onepager-title">{p.onePagerTitle}</SectionTitle>
          <p style={{ margin: "-6px 0 0", fontSize: 13, color: "var(--ink-faint)" }}>
            {fill(p.period, { from: formatDay(locale, model.period.from), to: formatDay(locale, model.period.to) })}
          </p>
        </div>
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            padding: "3px 10px",
            borderRadius: "var(--r-pill)",
            border: "1px solid var(--line-strong)",
            color: "var(--ink-soft)",
          }}
        >
          {p.sharedWithFamily}
        </span>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 12 }}>
        <Block title={p.supportTitle}>
          <ul style={listStyle}>
            {supportLines(p, model).map((l) => (
              <li key={l} style={lineStyle}>
                {l}
              </li>
            ))}
          </ul>
        </Block>
        <Block title={p.practiceTitle}>
          <ul style={listStyle}>
            {practiceLines(p, model).map((l) => (
              <li key={l} style={lineStyle}>
                {l}
              </li>
            ))}
          </ul>
        </Block>
      </div>

      {model.hasSteps && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 12 }}>
          <Block title={p.newTitle}>
            {model.newSince.length === 0 ? (
              <p style={{ ...lineStyle, margin: 0, color: "var(--ink-soft)" }}>{p.newNone}</p>
            ) : (
              <ul style={listStyle}>
                {model.newSince.map((s) => (
                  <li key={`${s.skillId}-${s.step}`} style={lineStyle}>
                    <b style={{ fontWeight: 600 }}>{stepLine(p, s)}</b>
                    {": "}
                    {stageLabel(p, s.stage)}
                    <span style={{ ...metaStyle, color: "var(--ink-soft)" }}>{s.canDo}</span>
                    <span style={metaStyle}>
                      {fill(p.reachedOn, { date: formatDay(locale, s.reachedOn) })} ·{" "}
                      {lastPractisedLine(p, locale, s.lastPractisedOn)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Block>
          <Block title={p.workingTitle}>
            {model.workingOn.length === 0 ? (
              <p style={{ ...lineStyle, margin: 0, color: "var(--ink-soft)" }}>{p.workingNone}</p>
            ) : (
              <ul style={listStyle}>
                {model.workingOn.map((s) => (
                  <li key={`${s.skillId}-${s.step}`} style={lineStyle}>
                    <b style={{ fontWeight: 600 }}>{stepLine(p, s)}</b>
                    <span style={{ ...metaStyle, color: "var(--ink-soft)" }}>{s.canDo}</span>
                    <span style={metaStyle}>
                      {s.since ? `${fill(p.since, { date: formatDay(locale, s.since) })} · ` : ""}
                      {lastPractisedLine(p, locale, s.lastPractisedOn)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Block>
        </div>
      )}

      <Block title={p.seenAtHomeTitle}>
        {model.seenAtHome.length === 0 ? (
          <p style={{ ...lineStyle, margin: 0, color: "var(--ink-soft)" }}>{p.seenAtHomeNone}</p>
        ) : (
          <>
            <ul style={listStyle}>
              {model.seenAtHome.map((n) => (
                <li key={n.id} style={lineStyle}>
                  <b style={{ fontWeight: 600 }}>{n.skillLabel}</b>
                  <span style={metaStyle}>{fill(p.seenAtHomeWeek, { date: formatDay(locale, n.weekOf) })}</span>
                  <span style={{ display: "block", marginTop: 4, color: "var(--ink)", overflowWrap: "anywhere" }}>{n.body}</span>
                </li>
              ))}
            </ul>
            <p style={{ margin: "10px 0 0", fontSize: 12.5, color: "var(--ink-faint)" }}>{p.seenAtHomeNote}</p>
          </>
        )}
      </Block>

      {model.hasSteps && model.areas.length > 0 && (
        <Block title={p.areasTitle}>
          <Dumbbells p={p} model={model} />
          <p style={{ margin: "12px 0 0", fontSize: 12.5, color: "var(--ink-faint)" }}>{p.areasNote}</p>
        </Block>
      )}
    </section>
  );
}
